import { useMemo, useState } from 'react';

import {
  Column,
  DataTable,
  EllipsisWithTooltip,
  ExternalLink,
  Icons,
  Input,
  Panel,
  tokens,
  Typography,
  Stack,
} from '@cast/design-system';

import { makeOptimizationWorkloadLink } from '@cast/split-me/features/cluster/workload-optimization';

import type { WorkloadRow } from './_types';

type ProposedPolicyWorkloadsTableProps = {
  policyName: string;
  clusterId: string;
  totalCount: number;
  rows: WorkloadRow[];
};

const WorkloadNameCell = ({
  name,
  kind,
  workloadId,
  clusterId,
}: Pick<WorkloadRow, 'name' | 'kind' | 'workloadId'> & {
  clusterId: string;
}) => {
  const kindLabel = (
    <Typography
      variant="P10R"
      sx={{ color: tokens.theme.fg.structural.secondary }}
    >
      {kind}
    </Typography>
  );

  if (!workloadId) {
    return (
      <Stack gap={0}>
        <EllipsisWithTooltip TypographyProps={{ variant: 'P12M' }}>
          {name}
        </EllipsisWithTooltip>
        {kindLabel}
      </Stack>
    );
  }

  return (
    <Stack gap={0}>
      <ExternalLink
        link={makeOptimizationWorkloadLink({ workloadId, clusterId })}
        iconProps={{ size: 14 }}
        sx={{
          gap: 2,
          '&:hover': { textDecoration: 'none' },
          typography: 'P12M',
        }}
      >
        {name}
      </ExternalLink>
      {kindLabel}
    </Stack>
  );
};

export const ProposedPolicyWorkloadsTable = ({
  policyName,
  clusterId,
  totalCount,
  rows,
}: ProposedPolicyWorkloadsTableProps) => {
  const [filter, setFilter] = useState('');

  const filteredRows = useMemo(() => {
    const query = filter.trim().toLowerCase();

    if (!query) {
      return rows;
    }

    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(query) ||
        row.namespace.toLowerCase().includes(query) ||
        row.kind.toLowerCase().includes(query)
    );
  }, [filter, rows]);

  const previewCount = rows.length;
  const remainingAfterApply = Math.max(totalCount - previewCount, 0);

  return (
    <Stack gap={12} data-testid={`policy-workloads-table-${policyName}`}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={12}
      >
        <Typography
          variant="A2"
          sx={{ color: tokens.theme.fg.structural.secondary }}
        >
          {totalCount} workloads
        </Typography>
        <Input
          placeholder="Search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          startAdornment={<Icons.MagnifyingGlass size={16} />}
          size="small"
          sx={{ width: 220 }}
          testId={`filter-workloads-${policyName}`}
        />
      </Stack>

      <Panel>
        <DataTable<WorkloadRow>
          data={filteredRows}
          sizing="m"
          rowKey="id"
          noShadow
          components={{
            noData: (
              <Typography
                variant="P12R"
                sx={{ color: tokens.theme.fg.structural.secondary, p: 12 }}
              >
                No workloads match your filter.
              </Typography>
            ),
          }}
        >
          <Column<WorkloadRow>
            id="workload"
            header="Workload"
            minWidth={160}
            sortable
            accessor="name"
            renderCell={(row) => (
              <WorkloadNameCell
                name={row.name}
                kind={row.kind}
                workloadId={row.workloadId}
                clusterId={clusterId}
              />
            )}
          />
          <Column<WorkloadRow>
            id="namespace"
            header="Namespace"
            minWidth={100}
            sortable
            accessor="namespace"
            renderCell={(row) => (
              <EllipsisWithTooltip TypographyProps={{ variant: 'P12R' }}>
                {row.namespace}
              </EllipsisWithTooltip>
            )}
          />
        </DataTable>
      </Panel>

      <Typography
        variant="P12R"
        sx={{ color: tokens.theme.fg.structural.secondary }}
      >
        Previewing{' '}
        <Typography component="span" variant="P12M">
          {previewCount}
        </Typography>{' '}
        of {totalCount} workloads
        {remainingAfterApply > 0 && (
          <> · {remainingAfterApply} more available after apply</>
        )}
      </Typography>
    </Stack>
  );
};
